import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  message,
  Radio,
  Row,
  Col,
  DatePicker,
  Typography,
  Select,
  Divider,
  Space,
} from "antd";
import axios from "axios";
import Constants from "../Utils/Constants";
import useToken from "../Authentication/useToken";
import dayjs from "dayjs";

const { Title } = Typography;

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #dbe3ef",
  borderRadius: 16,
  padding: 32,
  boxShadow: "0 10px 26px rgba(15, 35, 95, 0.06)",
};

const sectionTitleStyle = {
  fontSize: 15,
  fontWeight: 700,
  color: "#16325c",
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: "1px solid #edf1f7",
};

const labelStyle = {
  fontWeight: 600,
  color: "#1f2937",
};

const programOptions = [
  { label: "PACE", value: "PACE" },
  { label: "MATCH", value: "MATCH" },
  { label: "PATH", value: "PATH" },
];

const contactOptions = [
  { label: "Téléphone", value: "phone" },
  { label: "Courriel", value: "email" },
];

const DoctorEligibilityRequestForm = () => {
  const { token } = useToken();
  const [loading, setLoading] = useState(false);
  const [clinicOptions, setClinicOptions] = useState([]);
  const [form] = Form.useForm();
  const preferredContactMethod = Form.useWatch("preferredContactMethod", form) || "phone";

  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const res = await axios.get(`${Constants.SERVER_URL}/clinics?active=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const options = (res.data || [])
          .filter((clinic) => clinic?.name && clinic?.id)
          .map((clinic) => ({
            value: clinic.id,
            label: `${clinic.name}${clinic?.type ? ` (${clinic.type})` : ""}`,
          }));

        setClinicOptions(options);
      } catch (_error) {
        setClinicOptions([]);
      }
    };

    if (token) {
      fetchClinics();
    }
  }, [token]);

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const payload = {
        patientFirstname: values.patientFirstname,
        patientLastname: values.patientLastname,
        phoneNumber: values.preferredContactMethod === "phone" ? values.phoneNumber : null,
        email: values.preferredContactMethod === "email" ? values.email : null,
        preferredContactMethod: values.preferredContactMethod,
        prescribedProgram: values.prescribedProgram,
        isEligible: true,
        hasConsentToBeContacted: values.hasConsentToBeContacted,
        destinationClinicId: values.destinationClinicId,
        date: values.date ? values.date.format("YYYY-MM-DD") : null,
        comment: values.comment?.trim() || null,
      };

      const res = await axios.post(
        `${Constants.SERVER_URL}/eligibility-request`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const source = res?.data?.sourceClinicName ? `Source: ${res.data.sourceClinicName}` : "";
      const destination = res?.data?.destinationClinicName
        ? `Destination: ${res.data.destinationClinicName}`
        : "";
      const details = [source, destination].filter(Boolean).join(" | ");

      message.success(
        details
          ? `Demande envoyée avec succès. ${details}`
          : "Demande envoyée avec succès."
      );

      form.resetFields();
      form.setFieldsValue({
        date: dayjs(),
        preferredContactMethod: "phone",
        prescribedProgram: "PACE",
        hasConsentToBeContacted: true,
      });
    } catch (error) {
      console.error("Erreur lors de l'envoi :", error?.response?.data || error);
      message.error(
        error?.response?.data?.message || "Une erreur est survenue lors de l'envoi."
      );
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    const consentError = (errorInfo?.errorFields || []).find(
      (field) => Array.isArray(field?.name) && field.name.includes("hasConsentToBeContacted")
    );

    if (consentError) {
      message.error("Le consentement du patient est obligatoire pour envoyer la requête.");
      return;
    }

    message.error("Veuillez remplir tous les champs requis.");
  };

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "28px 24px 40px",
      }}
    >
      <div style={cardStyle}>
        <Title level={2} style={{ textAlign: "center", marginBottom: 28 }}>
          Formulaire d'éligibilité
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            date: dayjs(),
            preferredContactMethod: "phone",
            prescribedProgram: "PACE",
            hasConsentToBeContacted: true,
          }}
        >
          <div style={sectionTitleStyle}>Informations générales</div>

          <Row gutter={[18, 0]}>
            <Col xs={24} md={8}>
              <Form.Item
                label={<span style={labelStyle}>Date</span>}
                name="date"
                rules={[{ required: true, message: "Veuillez sélectionner la date" }]}
              >
                <DatePicker style={{ width: 180, maxWidth: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={<span style={labelStyle}>Nom du patient</span>}
                name="patientLastname"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input placeholder="Nom" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={<span style={labelStyle}>Prénom du patient</span>}
                name="patientFirstname"
                rules={[{ required: true, message: "Prénom requis" }]}
              >
                <Input placeholder="Prénom" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[18, 0]}>
            <Col xs={24} md={8}>
              <Form.Item
                label={<span style={labelStyle}>Mode de contact privilégié</span>}
                name="preferredContactMethod"
              >
                <Radio.Group options={contactOptions} optionType="button" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              {preferredContactMethod === "phone" ? (
                <Form.Item
                  label={<span style={labelStyle}>Téléphone</span>}
                  name="phoneNumber"
                  rules={[{ required: true, message: "Numéro de téléphone requis" }]}
                >
                  <Input placeholder="(514) 555-1234" />
                </Form.Item>
              ) : (
                <Form.Item
                  label={<span style={labelStyle}>Courriel</span>}
                  name="email"
                  rules={[
                    { required: true, message: "Courriel requis" },
                    { type: "email", message: "Veuillez entrer un courriel valide" },
                  ]}
                >
                  <Input placeholder="patient@exemple.com" />
                </Form.Item>
              )}
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={<span style={labelStyle}>Clinique de destination</span>}
                name="destinationClinicId"
                rules={[{ required: true, message: "Veuillez choisir une clinique" }]}
              >
                <Select
                  options={clinicOptions}
                  placeholder="Choisir le site"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "8px 0 28px" }} />

          <div style={sectionTitleStyle}>Prescription</div>

          <Row gutter={[18, 0]} align="top">
            <Col xs={24}>
              <Form.Item
                label={<span style={labelStyle}>Programme prescrit au patient</span>}
                name="prescribedProgram"
                rules={[{ required: true, message: "Veuillez choisir un programme" }]}
              >
                <Radio.Group style={{ width: "100%" }}>
                  <Space size={24} wrap>
                    {programOptions.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<span style={labelStyle}>Commentaires</span>}
            name="comment"
          >
            <Input.TextArea rows={4} placeholder="Contexte, notes ou précisions utiles" />
          </Form.Item>

          <Divider style={{ margin: "8px 0 28px" }} />

          <div style={sectionTitleStyle}>Consentement</div>

          <Form.Item
            label={<span style={labelStyle}>Le patient consent au transfert de la requête ?</span>}
            name="hasConsentToBeContacted"
            rules={[
              { required: true, message: "Veuillez choisir une option" },
              {
                validator: (_, value) =>
                  value === true
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          "Le consentement du patient est obligatoire pour envoyer la requête."
                        )
                      ),
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>Oui</Radio>
              <Radio value={false}>Non</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item style={{ textAlign: "center", marginTop: 36, marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              style={{ minWidth: 190, borderRadius: 10 }}
            >
              Envoyer la requête
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default DoctorEligibilityRequestForm;
